import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../lib/api.ts';
import { relativeTime } from '../lib/format.ts';
import { readSSE } from '../lib/sse.ts';
import Drawer from './Drawer.tsx';
import ConfirmModal from '../components/ConfirmModal.tsx';
import DeckVisibilityModal from '../components/DeckVisibilityModal.tsx';
import { currentUser } from '../lib/me.ts';
import type { Person } from '../../shared/organisation.ts';
import { Download, Check, X } from '../lib/icons.tsx';
import type {
  DecksPayload,
  DisplayDatePayload,
  PdfLogPayload,
  ValidationPayload,
  ViewPasswordPayload,
} from '../../shared/api.ts';

/**
 * What the build/publish stream emits. Every branch of the reader below reads
 * exactly one of these, so the union is what makes the narrowing honest — the
 * handler is handed `unknown` by `readSSE`, since the framing layer serves
 * three unrelated streams.
 */
type StreamEvent =
  | { type: 'stdout' | 'stderr'; text: string }
  | { type: 'done'; code: number }
  | { type: 'pdf-ready' | 'pdf-failed' }
  | { type: 'published'; url: string; publishedAt: string }
  | { type: 'error'; message: string };

/** Which action is streaming, and everything it has said so far. `done` may
 *  carry a synthetic code: -1 means the pipe died, not that the build failed. */
type Streaming = {
  kind: string;
  lines: string[];
  done?: { code: number };
  publish?: { url: string; publishedAt: string };
  error?: string;
};

const isStreamEvent = (p: unknown): p is StreamEvent =>
  !!p && typeof p === 'object' && typeof (p as { type?: unknown }).type === 'string';

// "February 2026" from a YYYY-MM-DD or full ISO string, formatted in UTC so the
// month never shifts with the viewer's timezone (matches the category page).
function monthYear(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? iso + 'T00:00:00Z' : iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** Validation + publish + build/download — v1 PublishPanel logic in drawer chrome. */
export default function PublishDrawer({
  slug,
  onClose,
  onJumpToSlide,
  revision,
}: {
  slug: string;
  onClose: () => void;
  /** Focus a slide in the filmstrip. A blocking finding must offer a way out,
   *  and "which slide" is the first thing you need. */
  onJumpToSlide?: (slideId: string) => void;
  /** Bumped by the editor whenever a slide changes, so the validation list
   *  re-reads instead of staying the snapshot it was on mount — fixing a slide
   *  used to leave its row on screen until the drawer was remounted. */
  revision?: number;
}) {
  const [validation, setValidation] = useState<ValidationPayload | null>(null);
  const [streaming, setStreaming] = useState<Streaming | null>(null);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [pdfCopied, setPdfCopied] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  // Last PDF run. The PDF is generated in the background after publish, so when
  // the public PDF link 404s this is the only place that says why.
  const [pdfInfo, setPdfInfo] = useState<PdfLogPayload | null>(null);
  const [pdfLogOpen, setPdfLogOpen] = useState(false);
  // Per-deck password on the /p/ link. `viewPassword` is the saved value;
  // `pwInput` the field being typed; `pwBusy` while saving.
  const [viewPassword, setViewPassword] = useState<string | null>(null);
  const [pwInput, setPwInput] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwRevealed, setPwRevealed] = useState(false);
  // Listing-date override (back-dating). `displayDate` is the saved override
  // (YYYY-MM-DD) or null; `dateInput` is the field value (defaults to the
  // publish date when no override is set).
  const [displayDate, setDisplayDate] = useState<string | null>(null);
  const [dateInput, setDateInput] = useState('');
  const [dateBusy, setDateBusy] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);
  // Privacy: who at RKH can open the deck. The public link below is the other
  // half of the same question, which is why the two sit in one drawer.
  const [title, setTitle] = useState('');
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [restrictedTo, setRestrictedTo] = useState<string[] | null>(null);
  const [people, setPeople] = useState<Person[]>([]);
  const [me, setMe] = useState<Person | null>(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);
  const logRef = useRef<HTMLPreElement>(null);

  const loadPdfInfo = useCallback(() => {
    api
      .get<PdfLogPayload>(`/api/build/${slug}/pdf-log`)
      .then(setPdfInfo)
      .catch(() => {});
  }, [slug]);

  // Validation re-reads on `revision` as well as on open: it is the one block in
  // here that goes stale the moment a slide is edited.
  useEffect(() => {
    api
      .get<ValidationPayload>(`/api/build/${slug}/validation`)
      .then(setValidation)
      .catch(() => {});
  }, [slug, revision]);

  useEffect(() => {
    api
      .get<Partial<DecksPayload>>('/api/decks')
      .then((data) => {
        const me = (data.decks || []).find((d) => d.slug === slug);
        if (me?.publishedUrl) setPublishedUrl(me.publishedUrl);
        if (me?.publishedAt) setPublishedAt(me.publishedAt);
        if (me?.viewPassword) setViewPassword(me.viewPassword);
        if (me) {
          setDisplayDate(me.displayDate || null);
          setDateInput(me.displayDate || (me.publishedAt ? me.publishedAt.slice(0, 10) : ''));
          setTitle(me.title);
          setOwnerId(me.ownerId);
          setRestrictedTo(me.restrictedTo);
        }
        setPeople(data.people || []);
      })
      .catch(() => {});
    currentUser().then((who) => setMe(who?.person ?? null));
    loadPdfInfo();
  }, [slug, loadPdfInfo]);

  async function savePrivacy(next: string[] | null) {
    setPrivacyBusy(true);
    setPrivacyError(null);
    try {
      const res = await api.put<{ restrictedTo: string[] | null; ownerId: string | null }>(
        `/api/decks/${slug}/visibility`,
        { restrictedTo: next },
      );
      setRestrictedTo(res.restrictedTo);
      setOwnerId(res.ownerId);
      setPrivacyOpen(false);
    } catch (err) {
      setPrivacyError(errorMessage(err));
    } finally {
      setPrivacyBusy(false);
    }
  }

  async function saveDisplayDate(value: string | null) {
    setDateBusy(true);
    setDateError(null);
    try {
      const res = await api.put<DisplayDatePayload>(`/api/decks/${slug}/display-date`, { displayDate: value });
      setDisplayDate(res.displayDate);
      if (!res.displayDate) setDateInput(publishedAt ? publishedAt.slice(0, 10) : '');
    } catch (err) {
      setDateError(errorMessage(err));
    } finally {
      setDateBusy(false);
    }
  }

  async function savePassword(password: string | null) {
    setPwBusy(true);
    setPwError(null);
    try {
      const res = await api.put<ViewPasswordPayload>(`/api/decks/${slug}/view-password`, { password });
      setViewPassword(res.viewPassword);
      setPwInput('');
      setPwRevealed(false);
    } catch (err) {
      setPwError(errorMessage(err));
    } finally {
      setPwBusy(false);
    }
  }

  function copyLink() {
    if (!publishedUrl) return;
    navigator.clipboard.writeText(publishedUrl).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  }

  async function streamPost(path: string, kind: string) {
    setStreaming({ kind, lines: [] });
    try {
      const res = await fetch(path, { method: 'POST', credentials: 'same-origin' });
      if (!res.ok) {
        const err = await res.text();
        setStreaming((s) =>
          s ? { ...s, lines: [...s.lines, '[' + res.status + '] ' + err], done: { code: res.status } } : null,
        );
        return;
      }
      let sawDone = false;
      // stallMs: the server sends a keepalive every 20s precisely so a
      // half-open connection is detectable. The publish stream deliberately
      // stays open past `done` for the PDF pass, so the window is generous —
      // it only has to be shorter than "forever".
      await readSSE(
        res,
        (payload) => {
          if (!isStreamEvent(payload)) return;
          if (payload.type === 'stdout' || payload.type === 'stderr') {
            setStreaming((s) => (s ? { ...s, lines: [...s.lines, payload.text] } : null));
          } else if (payload.type === 'done') {
            sawDone = true;
            setStreaming((s) => (s ? { ...s, done: payload } : null));
            loadPdfInfo();
          } else if (payload.type === 'pdf-ready' || payload.type === 'pdf-failed') {
            loadPdfInfo();
          } else if (payload.type === 'published') {
            setPublishedUrl(payload.url);
            setPublishedAt(payload.publishedAt);
            setStreaming((s) => (s ? { ...s, publish: payload } : null));
          } else if (payload.type === 'error') {
            setStreaming((s) => (s ? { ...s, error: payload.message } : null));
          }
        },
        { stallMs: 120_000 },
      );
      // The stream ended without a `done`. isPublishing keys off `done` being
      // absent, so ignoring this pinned the Publish button on "Publishing…"
      // and disabled — permanently, with no error, until a page reload. The
      // server keeps going regardless (its abort handler is a no-op), so say
      // that rather than implying the publish failed. Re-read the PDF status
      // too: the render outlives this connection and may already have landed.
      if (!sawDone) {
        setStreaming((s) =>
          s
            ? {
                ...s,
                lines: [
                  ...s.lines,
                  'Lost the connection to the server. This may still have finished; reopen to check.',
                ],
                done: { code: -1 },
              }
            : null,
        );
        loadPdfInfo();
      }
    } catch (err) {
      setStreaming((s) =>
        s ? { ...s, lines: [...s.lines, 'Stream error: ' + errorMessage(err)], done: { code: -1 } } : null,
      );
    }
  }

  async function unpublish() {
    if (!window.confirm('Unpublish this deck? The /p/ URL will return 404 until you publish again.')) return;
    setUnpublishing(true);
    try {
      await api.post(`/api/build/${slug}/unpublish`);
      setPublishedUrl(null);
      setPublishedAt(null);
      setStreaming(null);
    } catch (err) {
      window.alert('Unpublish failed: ' + errorMessage(err));
    } finally {
      setUnpublishing(false);
    }
  }

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [streaming]);

  const errCount = validation?.errorCount ?? 0;
  // Split by severity so the blocking findings can be stated once, prominently,
  // instead of sitting in rank order among the advisories.
  const blocking = (validation?.issues || []).filter((i) => i.severity === 'error');
  const [confirmPublish, setConfirmPublish] = useState(false);
  const advisories = (validation?.issues || []).filter((i) => i.severity !== 'error');
  const isPublishing = streaming?.kind === 'publish' && !streaming?.done && !streaming?.error;
  const publishedNow = Boolean(publishedAt);
  // The published PDF is served statically alongside the deck at
  // /p/<slug>/<deckname>.pdf — inline-viewable (no auth, opens in the browser).
  const pdfUrl = publishedUrl ? `${publishedUrl.replace(/\/$/, '')}/${slug.split('/').pop()}.pdf` : null;
  const pdfBuilding = pdfInfo?.status?.state === 'running';
  // Until we've asked the server, assume the link is fine (no flash of "broken"
  // on a deck whose PDF is there).
  const pdfHealthy = !pdfInfo || pdfInfo.exists;

  return (
    <Drawer title="Publish" onClose={onClose} wide>
      <section className="publish__section">
        <h3>Validation</h3>
        {!validation && <p className="muted small">Loading…</p>}
        {validation && validation.issues.length === 0 && <p className="muted small">No issues found.</p>}
        {/* Blocking findings get their own box above the list. They are not style
            advisories: they mean the deck would go out asserting something untrue
            (other companies' logos as clients, a colleague's face under someone
            else's name), so they read as a stated problem with a way out rather
            than a grey row in a list of thirty. */}
        {blocking.length > 0 && (
          <div className="publish__blockers" role="alert">
            {blocking.map((i, idx) => (
              <div key={idx} className="publish__blocker">
                <p>
                  <strong>Slide {i.idx + 1}</strong> {i.message}
                </p>
                {onJumpToSlide && (
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => {
                      onJumpToSlide(i.slide);
                      onClose();
                    }}
                  >
                    Go to slide {i.idx + 1}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {advisories.length > 0 && (
          <ul className="publish__issues">
            {advisories.slice(0, 50).map((i, idx) => (
              <li key={idx} className={'publish__issue publish__issue--' + i.severity}>
                <span className="publish__issue-num">{String(i.idx + 1).padStart(2, '0')}</span>
                <span className="publish__issue-msg">{i.message}</span>
              </li>
            ))}
            {advisories.length > 50 && <li className="muted small">…and {advisories.length - 50} more</li>}
          </ul>
        )}
      </section>

      {/* Above the public link deliberately: "who at RKH" then "who outside".
          The control itself is the dashboard's dialog rather than a second copy
          of it — one setting rendered twice is two places to keep in step. */}
      <section className="publish__section">
        <h3>Privacy</h3>
        <p className="muted small">
          {restrictedTo === null
            ? 'Anyone signed in to Studio can open this deck.'
            : restrictedTo.length === 0
              ? `Only ${ownerId && ownerId === me?.id ? 'you' : (people.find((p) => p.id === ownerId)?.name ?? 'its owner')} and studio admins can open it.`
              : `Shared with ${restrictedTo.length} ${restrictedTo.length === 1 ? 'person' : 'people'}, plus its owner and studio admins.`}{' '}
          The public link below is separate and is not affected.
        </p>
        {privacyError && <p className="error small">{privacyError}</p>}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPrivacyOpen(true)}>
          Change…
        </button>
        {privacyOpen && (
          <DeckVisibilityModal
            deck={{ title, ownerId, restrictedTo }}
            people={people}
            me={me}
            busy={privacyBusy}
            nested
            onCancel={() => setPrivacyOpen(false)}
            onSave={savePrivacy}
          />
        )}
      </section>

      <section className="publish__section">
        <h3>Publish to public URL</h3>
        <p className="muted small">
          Builds the deck and serves it at <code>/p/{slug}/</code> on this studio.{' '}
          {viewPassword ? 'Viewers need the password below.' : 'Anyone with the link can view.'}
        </p>

        {/* Keyed off `publishedAt` rather than the `publishedNow` boolean it
            derives, so the "last published" line below reads a date the
            checker knows is there. The two conditions are the same one. */}
        {publishedAt && (
          <div className="publish__shared">
            <div className="publish__url">
              <span className="muted small">Live at</span>
              <a href={publishedUrl ?? undefined} target="_blank" rel="noopener noreferrer" className="link">
                {publishedUrl}
              </a>
            </div>
            {pdfUrl && pdfHealthy && (
              <div className="publish__url">
                <span className="muted small">PDF</span>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="link">
                  {pdfUrl}
                </a>
              </div>
            )}
            {/* No PDF on disk means that link 404s — say so, with the reason,
                instead of handing out a broken URL. */}
            {pdfUrl && !pdfHealthy && (
              <div className="publish__pdf-problem">
                <p className="small">
                  {pdfBuilding
                    ? 'PDF is still generating…'
                    : 'No PDF for this version yet, so the PDF link would 404. Run “Build HTML + PDF” below to retry.'}
                </p>
                {pdfInfo?.log && (
                  <>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPdfLogOpen((v) => !v)}>
                      {pdfLogOpen ? 'Hide PDF build log' : 'Why? Show PDF build log'}
                    </button>
                    {pdfLogOpen && <pre className="publish__log">{pdfInfo.log}</pre>}
                  </>
                )}
              </div>
            )}
            <p className="muted small">Last published {relativeTime(new Date(publishedAt).getTime())}</p>
            <div className="publish__shared-actions">
              <button type="button" className="btn btn--ghost btn--sm" onClick={copyLink}>
                {copied ? 'Copied' : 'Copy link'}
              </button>
              {pdfUrl && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() =>
                    navigator.clipboard.writeText(pdfUrl).then(
                      () => {
                        setPdfCopied(true);
                        setTimeout(() => setPdfCopied(false), 1500);
                      },
                      () => {},
                    )
                  }
                >
                  {pdfCopied ? 'Copied' : 'Copy PDF link'}
                </button>
              )}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={unpublish}
                disabled={unpublishing || isPublishing}
              >
                {unpublishing ? 'Unpublishing…' : 'Unpublish'}
              </button>
            </div>
          </div>
        )}

        <div className="publish__actions">
          {/* A blocking finding stops the one-click path but never the author:
              "a warning must always offer a way out" (docs/edit-codecs.md), and
              a button that can only be obeyed is not a way out. Shipping the
              demo logos is almost never right, but the person looking at the
              deck is the one who knows (Alex, 2026-08-18). */}
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() =>
              errCount > 0 ? setConfirmPublish(true) : streamPost(`/api/build/${slug}/publish`, 'publish')
            }
            disabled={isPublishing || unpublishing}
            title={errCount > 0 ? 'There are validation errors: you will be asked to confirm' : ''}
          >
            {isPublishing
              ? 'Publishing…'
              : errCount > 0
                ? `Publish with ${errCount} problem${errCount === 1 ? '' : 's'}…`
                : publishedNow
                  ? 'Re-publish'
                  : 'Publish now'}
          </button>
        </div>
        {/* Nothing is persisted by this: the block returns next publish, which
            for "this deck shows other companies' logos" is the right default,
            and it keeps the filmstrip's Check tag honest — nothing was waived,
            only overridden once. "Publish anyway" is the SECONDARY action
            because ConfirmModal fires the primary on Enter. */}
        {confirmPublish && (
          <ConfirmModal
            title={`Publish with ${blocking.length} unresolved problem${blocking.length === 1 ? '' : 's'}?`}
            body={
              <>
                <p>This deck will go out as it stands:</p>
                <ul className="publish__issues">
                  {blocking.map((i, idx) => (
                    <li key={idx} className="publish__issue">
                      <span className="publish__issue-msg">
                        <strong>Slide {i.idx + 1}</strong> {i.message}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            }
            confirmLabel="Go back and fix"
            onConfirm={() => setConfirmPublish(false)}
            onCancel={() => setConfirmPublish(false)}
            secondaryLabel="Publish anyway"
            onSecondary={() => {
              setConfirmPublish(false);
              streamPost(`/api/build/${slug}/publish`, 'publish');
            }}
          />
        )}

        {streaming?.error && <p className="error">{streaming.error}</p>}
      </section>

      <section className="publish__section">
        <h3>Listing date</h3>
        <p className="muted small">
          The date this deck sorts under on category pages (<code>/c/…</code>). Defaults to when it was published:
          back-date a manually-added older deck to its real date.
        </p>
        <form
          className="publish__shared-actions"
          style={{ alignItems: 'center', gap: '0.6rem' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (dateInput) saveDisplayDate(dateInput);
          }}
        >
          <input type="date" value={dateInput} onChange={(e) => setDateInput(e.target.value)} disabled={dateBusy} />
          <button type="submit" className="btn btn--primary btn--sm" disabled={dateBusy || !dateInput}>
            {dateBusy ? 'Saving…' : 'Set date'}
          </button>
          {displayDate && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={dateBusy}
              onClick={() => saveDisplayDate(null)}
            >
              Reset to publish date
            </button>
          )}
        </form>
        {displayDate ? (
          <p className="muted small">
            Listed under <strong>{monthYear(displayDate)}</strong> (back-dated).
          </p>
        ) : publishedAt ? (
          <p className="muted small">
            Listed under <strong>{monthYear(publishedAt)}</strong> (publish date).
          </p>
        ) : (
          <p className="muted small">Publish the deck to list it on a category page.</p>
        )}
        {dateError && <p className="error">{dateError}</p>}
      </section>

      <section className="publish__section">
        <h3>Password protection</h3>
        {viewPassword ? (
          <>
            <p className="muted small">
              The published link asks viewers for a password before showing anything (slides, assets and the PDF
              included).
            </p>
            <div className="publish__shared-actions" style={{ alignItems: 'center', gap: '0.6rem' }}>
              <code>{pwRevealed ? viewPassword : '•'.repeat(Math.min(viewPassword.length, 12))}</code>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPwRevealed((v) => !v)}>
                {pwRevealed ? 'Hide' : 'Reveal'}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => navigator.clipboard.writeText(viewPassword).catch(() => {})}
              >
                Copy password
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={pwBusy}
                onClick={() => {
                  if (window.confirm('Remove the password? Anyone with the link will be able to view the deck.'))
                    savePassword(null);
                }}
              >
                Remove
              </button>
            </div>
            <p className="muted small">Changing or removing the password signs every current viewer out immediately.</p>
          </>
        ) : (
          <p className="muted small">No password: anyone with the link can view. Set one to protect the deck.</p>
        )}
        <form
          className="publish__shared-actions"
          style={{ alignItems: 'center', gap: '0.6rem' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (pwInput.trim()) savePassword(pwInput);
          }}
        >
          <input
            type="text"
            value={pwInput}
            placeholder={viewPassword ? 'New password…' : 'Choose a password…'}
            onChange={(e) => setPwInput(e.target.value)}
            disabled={pwBusy}
            style={{ flex: 1, minWidth: '12rem' }}
          />
          <button type="submit" className="btn btn--primary btn--sm" disabled={pwBusy || !pwInput.trim()}>
            {pwBusy ? 'Saving…' : viewPassword ? 'Change password' : 'Set password'}
          </button>
        </form>
        {pwError && <p className="error">{pwError}</p>}
      </section>

      <section className="publish__section">
        <h3>Build &amp; download</h3>
        <div className="publish__actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => streamPost(`/api/build/${slug}/check-overflow`, 'check')}
          >
            Check overflow
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => streamPost(`/api/build/${slug}/build`, 'build-html')}
          >
            Build HTML
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => streamPost(`/api/build/${slug}/build-pdf`, 'build-full')}
          >
            Build HTML + PDF
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => streamPost(`/api/build/${slug}/build-portable`, 'build-portable')}
          >
            Build portable
          </button>
        </div>
        <div className="publish__group">
          <p className="publish__group-head">
            Share <span className="muted">(view only, cannot be edited)</span>
          </p>
          <div className="publish__downloads">
            <span className="publish__dl">
              <a href={`/api/build/${slug}/download/portable`} download className="link">
                <Download /> Portable deck
              </a>
              <small className="muted">double-click to open, no server</small>
            </span>
            <span className="publish__dl">
              <a href={`/api/build/${slug}/download/build`} download className="link">
                <Download /> Web build
              </a>
              <small className="muted">for hosting on a server</small>
            </span>
            <span className="publish__dl">
              <a href={`/api/build/${slug}/download/pdf`} download className="link">
                <Download /> PDF
              </a>
              <small className="muted">printable document</small>
            </span>
          </div>
        </div>
        <div className="publish__group">
          <p className="publish__group-head">
            Edit elsewhere <span className="muted">(re-importable)</span>
          </p>
          <div className="publish__downloads">
            <span className="publish__dl">
              <a href={`/api/decks/${slug}/export`} download className="link">
                <Download /> Editable copy (.zip)
              </a>
              <small className="muted">import into any Studio to keep editing</small>
            </span>
          </div>
        </div>
      </section>

      {streaming && streaming.lines.length > 0 && (
        <section className="publish__section">
          <h3>
            {streaming.kind.charAt(0).toUpperCase() + streaming.kind.slice(1)} log{' '}
            {streaming.done &&
              (streaming.done.code === 0 ? <Check className="icon-inline" /> : <X className="icon-inline" />)}
          </h3>
          <pre className="publish__log" ref={logRef}>
            {streaming.lines.join('')}
          </pre>
        </section>
      )}
    </Drawer>
  );
}
